import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  FormControl,
  FormLabel,
  InputAdornment,
  SxProps,
  OutlinedInput,
  Menu,
  Stack,
  Box,
  Typography,
} from '@mui/material';
import Button from '~/components/common/Button';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HoursSelect } from '~/pages/AdminOptions/Stores/Detail/components/DeliveryHours/components/HoursSelect';
import themes from '~/themes';
import moment from 'moment';
import CloseIcon from '~/assets/images/icons/close.svg';
import { isEqual } from 'lodash';
import { formatAmPm } from '~/utils/formatter';

export interface TimeSelectorProps {
  value?: null | {
    from: string;
    to: string;
  };
  legend?: string;
  onChange?: (value: any) => void;
  placeholder?: string;
  fullWidth?: boolean;
  sxLabel?: SxProps;
  onClear?: () => void;
}

export const TimeRangePicker: React.FC<TimeSelectorProps> = ({
  value,
  legend,
  placeholder = 'Time',
  onChange,
  fullWidth = true,
  sxLabel,
  onClear,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [timeRange, setTimeRange] = useState(value);
  const anchorElRef = useRef(null);

  const isValidTimeRange = useMemo(() => {
    if (!timeRange.from && !timeRange.to) return false;
    if (timeRange.from && timeRange.to) {
      const from = moment(timeRange.from, 'hh:mm a');
      const to = moment(timeRange.to, 'hh:mm a');
      return from.isBefore(to);
    }
    return true;
  }, [timeRange]);

  const textValue = useMemo(() => {
    const { from, to } = value || {};
    const formatTime = (time) =>
      formatAmPm(moment(time, 'hh:mm a').format('h:mm a'));
    if (from && to) {
      return `${formatTime(from)} - ${formatTime(to)}`;
    }
    if (from) return formatTime(from);
    if (to) return formatTime(to);
    return '';
  }, [value]);

  useEffect(() => {
    setTimeRange(value);
  }, [value]);

  const renderEndAdornment = () => {
    if (textValue)
      return (
        <InputAdornment
          position='end'
          variant='filled'
          sx={{
            paddingRight: '14px',
            paddingLeft: '14px',
          }}
          onClick={(e) => {
            e.stopPropagation();
            setTimeRange({
              from: '',
              to: '',
            });
            onClear();
          }}
        >
          <img alt='clear-icon' src={CloseIcon} width={10} height={11} />
        </InputAdornment>
      );

    return (
      <InputAdornment
        position='end'
        variant='filled'
        sx={{
          paddingRight: '7px',
        }}
      >
        {isOpen ? (
          <KeyboardArrowUp
            sx={{ color: `${themes.color.violet900} !important` }}
          />
        ) : (
          <KeyboardArrowDown
            sx={{ color: `${themes.color.violet900} !important` }}
          />
        )}
      </InputAdornment>
    );
  };

  return (
    <FormControl fullWidth={fullWidth} variant='standard'>
      {legend &&
        (typeof legend === 'string' ? (
          <FormLabel sx={sxLabel}>{legend}</FormLabel>
        ) : (
          legend
        ))}
      <OutlinedInput
        ref={anchorElRef}
        sx={{
          cursor: 'pointer',
          input: {
            cursor: 'pointer',
            overflow: 'hidden !important',
            textOverflow: 'ellipsis !important',
            whiteSpace: 'nowrap !important',
          },
        }}
        value={textValue}
        readOnly
        placeholder={placeholder}
        endAdornment={renderEndAdornment()}
        onClick={() => {
          setIsOpen((prevState) => !prevState);
        }}
      />
      <Menu
        anchorEl={anchorElRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        MenuListProps={{
          sx: {
            padding: 0,
          },
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '8px',
              p: 2.4,
              boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.16)',
            },
          },
        }}
      >
        <Stack gap={1.6}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1.6,
            }}
          >
            <HoursSelect
              legend='From time'
              placeholder='Select option'
              paperMaxHeight={200}
              timeGap={60}
              sxFormControl={{
                '& > div': {
                  background: '#F8F8FC',
                  color: timeRange.from ? 'black' : 'rgba(51, 54, 61, 0.40)',
                },
              }}
              timeFormat='h:mm a'
              value={timeRange.from}
              onChange={(e: any) => {
                setTimeRange((prev) => ({ ...prev, from: e.target.value }));
              }}
              error={timeRange.from && !isValidTimeRange}
            />
            <HoursSelect
              legend='To time'
              placeholder='Select option'
              paperMaxHeight={200}
              timeGap={60}
              sxFormControl={{
                '& > div': {
                  background: '#F8F8FC',
                  color: timeRange.to ? 'black' : 'rgba(51, 54, 61, 0.40)',
                },
              }}
              timeFormat='h:mm a'
              value={timeRange.to}
              onChange={(e: any) => {
                setTimeRange((prev) => ({ ...prev, to: e.target.value }));
              }}
              error={timeRange.to && !isValidTimeRange}
            />
          </Box>
          <Box
            justifyContent='flex-end'
            flexDirection='row'
            display='flex'
            gap={1.2}
          >
            <Button
              buttonType='text'
              noRounder
              sx={{
                minWidth: 'unset !important',
                padding: '4px 16px',
                marginRight: '0px !important',
              }}
              onClick={() => {
                setTimeRange({
                  from: '',
                  to: '',
                });
              }}
            >
              <Typography
                sx={{
                  color: themes.color.violet500,
                  fontWeight: 500,
                }}
              >
                Clear
              </Typography>
            </Button>
            <Button
              noRounder
              buttonType='primary-dark'
              sx={{
                minWidth: 'unset !important',
                padding: '4px 16px',
              }}
              disabled={
                isEqual(value, timeRange) ||
                (!isValidTimeRange &&
                  !(timeRange.from === '' && timeRange.to === ''))
              }
              onClick={() => {
                onChange(timeRange);
                setIsOpen(false);
              }}
            >
              <Typography
                sx={{
                  minWidth: 'unset !important',
                  fontWeight: 500,
                }}
              >
                Apply
              </Typography>
            </Button>
          </Box>
        </Stack>
      </Menu>
    </FormControl>
  );
};
